import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BookingFormModal from "@/pages/excursion/components/BookingFormModal";

interface Variant {
  id: string;
  label: string;
  price: number;
  child?: number;
  infant?: number;
}

interface Props {
  variants: Variant[];
  slug: string;
  title: string;
}

export default function BookingWidget({ variants, slug, title }: Props) {
  const { t } = useTranslation();
  const [variantId, setVariantId] = useState(variants[0].id);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [date, setDate] = useState("");
  const [showForm, setShowForm] = useState(false);

  const variant = useMemo(
    () => variants.find((v) => v.id === variantId) ?? variants[0],
    [variantId, variants]
  );

  const total = useMemo(() => {
    const adultPrice = variant.price;
    const childPrice = variant.child ?? Math.round(variant.price * 0.75);
    const infantPrice = variant.infant ?? 0;
    return adults * adultPrice + children * childPrice + infants * infantPrice;
  }, [adults, children, infants, variant]);

  function Counter({
    value,
    onChange,
    min = 0,
  }: {
    value: number;
    onChange: (n: number) => void;
    min?: number;
  }) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-8 h-8 rounded-md border border-background-300 hover:bg-background-100 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-subtract-line text-foreground-800"></i>
        </button>
        <div className="w-9 text-center font-medium text-foreground-950">{value}</div>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 rounded-md border border-background-300 hover:bg-background-100 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-add-line text-foreground-800"></i>
        </button>
      </div>
    );
  }

  function handleAdd() {
    setShowForm(true);
  }

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 lg:sticky lg:top-24">
      <div className="mb-5">
        <div className="text-xs uppercase tracking-wider text-foreground-500 mb-1">{t("bw_manage")}</div>
        <div className="font-heading text-xl font-semibold text-foreground-950">{t("bw_book")} {title.split("—")[0].trim()}</div>
      </div>

      <div className="mb-5">
        <label className="block text-xs font-medium text-foreground-700 mb-2">{t("bw_option")}</label>
        <div className="space-y-2">
          {variants.map((v, idx) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVariantId(v.id)}
              className={`w-full flex items-center justify-between p-3 rounded-md border text-left transition-colors cursor-pointer ${
                variantId === v.id
                  ? "border-accent-500 bg-accent-50/70"
                  : "border-background-200 hover:border-background-400"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    variantId === v.id ? "border-primary-500" : "border-background-400"
                  }`}
                >
                  {variantId === v.id && <div className="w-2 h-2 rounded-full bg-accent-500"></div>}
                </div>
                <span className="text-sm font-medium text-foreground-900">{t(`ex_var_${slug}_${idx}`)}</span>
              </div>
              <span className="text-sm font-semibold text-accent-700">€{v.price}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-xs font-medium text-foreground-700 mb-2">{t("bw_date")}</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </div>

      <div className="mb-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-foreground-900">{t("bw_adults")}</div>
            <div className="text-xs text-foreground-500">€{variant.price}</div>
          </div>
          <Counter value={adults} onChange={setAdults} min={1} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-foreground-900">{t("bw_children")}</div>
            <div className="text-xs text-foreground-500">€{variant.child ?? Math.round(variant.price * 0.75)}</div>
          </div>
          <Counter value={children} onChange={setChildren} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-foreground-900">{t("bw_infants")}</div>
            <div className="text-xs text-foreground-500">€{variant.infant ?? 0}</div>
          </div>
          <Counter value={infants} onChange={setInfants} />
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-background-200/70 mb-4">
        <span className="text-sm font-medium text-foreground-800">{t("bw_total")}</span>
        <span className="font-heading text-2xl font-semibold text-accent-700">€{total.toFixed(2)}</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={handleAdd}
          data-product-shop
          data-slug={slug}
          className="bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
        >
          {t("bw_add_cart")}
        </button>
        <button
          onClick={handleAdd}
          data-product-shop
          data-slug={slug}
          className="bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
        >
          {t("bw_book_now")}
        </button>
      </div>

      {showForm && (
        <BookingFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          data={{
            excursionSlug: slug,
            excursionTitle: title,
            variantLabel: variant.label,
            variantPrice: variant.price,
            adults,
            children,
            infants,
            total,
            date,
          }}
        />
      )}

      <div className="mt-5 pt-5 border-t border-background-200/70 space-y-2 text-xs text-foreground-600">
        <div className="flex items-start gap-2">
          <i className="ri-whatsapp-line text-accent-500 mt-0.5"></i>
          <span>{t("bw_whatsapp_support")} +48 792 842 613</span>
        </div>
        <div className="flex items-start gap-2">
          <i className="ri-phone-line text-primary-500 mt-0.5"></i>
          <a href="tel:+34604150904" className="hover:text-accent-600 cursor-pointer transition-colors">{t("bw_local_phone")} +34 604 150 904</a>
        </div>
        <div className="flex items-start gap-2">
          <i className="ri-shield-check-line text-accent-500 mt-0.5"></i>
          <span>{t("bw_instant")}</span>
        </div>
      </div>
    </div>
  );
}